import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { FRPConst } from "./FRPConst";
import { Localization } from "@viamap/viamap2-common";
import { useContext, useEffect, useState } from "react";
import { FRPContext, setVisualizationId } from "./FRPState";
import { GlassInputControl } from "src/components/GlassInput";
import { GenerateGeom } from "src/managers/GenerateGeom";
import { ApplicationStateContext } from "src/states/ApplicationState";
import chroma from "chroma-js";

export function FRPLegendDialog() {
   const { state: frpState } = useContext(FRPContext);

   return (
      <AdvancedDragModal variant={"Small"} title={Localization.getText("Frp Legend")}
         PosDefault={{ "left": "calc(100% - max(25%, 320px) - 20px)", top: "calc(100% - 10px)", transform: "translate(-100%, -100%)" }}
      >
         <ADModalBody>
            <FRPLegendByVisualization visualization={frpState.visualizationId} />
         </ADModalBody>
      </AdvancedDragModal>
   )
}

export function FRPLegendList() {
   const { state: frpState } = useContext(FRPContext);

   return (
      <>
         <div style={{ marginLeft: "24px" }}>
            <FRPLegendByVisualization visualization={frpState.visualizationId} />
         </div>
      </>
   )

}


export function FRPLegendLine(props: { color: string, da: string, en: string }) {
   return (
      <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
         <div style={{ width: "16px", height: "16px", backgroundColor: props.color, marginLeft: "2px", marginRight: "6px", borderRadius: "3px", border: "1px solid rgba(128, 128, 128, 0.3)" }}></div>
         <div style={{ fontSize: "12px" }}>{props[Localization.getLanguage()]}</div>
      </div>
   )
}

export function FRPLegendMitPattern(props: { pattern: string, da: string, en: string }) {
   let [imgUrl, setImgUrl] = useState<string | undefined>(undefined);


   useEffect(() => {
      GenerateGeom.getEveryMitPattern(props.pattern, (url) => {
         setImgUrl(url);
      });
   }, [])

   return (
      <div style={{ display: "flex", alignItems: "center", marginBottom: "2px" }}>
         <div style={{ width: "16px", height: "16px", backgroundImage: `url(${imgUrl})`, backgroundSize: "cover", marginLeft: "2px", marginRight: "6px", borderRadius: "3px", border: "1px solid rgba(128, 128, 128, 0.3)" }}></div>
         <div style={{ fontSize: "12px" }}>{props[Localization.getLanguage()]}</div>
      </div>
   )

}

export function FRPLegendByVisualization(props: { visualization: typeof FRPConst.visualizations[number]["id"] }) {
   const { state: FRPState, dispatch: frpDispatch } = useContext(FRPContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);

   return (
      <>
         <div style={{ maxHeight: "1.em", marginBlock: "2px", margin: "4px 40px 12px 0px" }}>
            <GlassInputControl>
               <select style={{ textAlign: "left" }} className="FRP_visualizationSelect" value={props.visualization} onChange={(e) => {
                  frpDispatch(setVisualizationId(e.target.value));
               }}>
                  {FRPConst.visualizations
                     .filter((a) => !a.inactive && hasAccessToFeature(a.access))
                     .map((visualization) => (
                        <option key={visualization.id} value={visualization.id}>
                           {visualization[Localization.getLanguage()]}
                        </option>
                     ))}
               </select>
            </GlassInputControl>
         </div>
         {(() => {
            switch (props.visualization) {
               case "status":
                  return (
                     <div>
                        {FRPState.statusOptions.map((element) => (
                           <FRPLegendLine key={element.id} color={element.color} da={element.val} en={element.val} />
                        ))}
                        <FRPLegendLine color={FRPConst.defaultColor} da="Ingen status" en="No status" />
                     </div>
                  );
               case "progress": {
                  // Helper function to convert rgba to hex for pattern names
                  const rgbaToHexForPattern = (rgbaColor: string): string => {
                     try {
                        if (rgbaColor.startsWith("#")) {
                           return rgbaColor;
                        }
                        const match = rgbaColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
                        if (match) {
                           const r = parseInt(match[1]).toString(16).padStart(2, '0');
                           const g = parseInt(match[2]).toString(16).padStart(2, '0');
                           const b = parseInt(match[3]).toString(16).padStart(2, '0');
                           return `#${r}${g}${b}`;
                        }
                        return chroma(rgbaColor).hex();
                     } catch {
                        return "#EEEEEE";
                     }
                  };

                  const stripeColor = rgbaToHexForPattern(FRPConst.progressColors.stripe);
                  const backgroundColor = rgbaToHexForPattern(FRPConst.progressColors.background);
                  const patternBase = `mit-patt-pdiag-`;

                  return (
                     <div>
                        <FRPLegendMitPattern pattern={patternBase + "0-" + stripeColor + "-" + backgroundColor} da="0% Færdig" en="0% Complete" />
                        <FRPLegendMitPattern pattern={patternBase + "25-" + stripeColor + "-" + backgroundColor} da="25% Færdig" en="25% Complete" />
                        <FRPLegendMitPattern pattern={patternBase + "50-" + stripeColor + "-" + backgroundColor} da="50% Færdig" en="50% Complete" />
                        <FRPLegendMitPattern pattern={patternBase + "75-" + stripeColor + "-" + backgroundColor} da="75% Færdig" en="75% Complete" />
                        <FRPLegendMitPattern pattern={patternBase + "100-" + stripeColor + "-" + backgroundColor} da="100% Færdig" en="100% Complete" />
                        <FRPLegendMitPattern pattern={patternBase + "0-" + stripeColor + "-" + backgroundColor} da="Ikke angivet" en="Not specified" />
                     </div>
                  )
               }
               case "processStatus":
                  return (
                     <div>
                        <FRPLegendLine color={FRPConst.processStatusColors.folgtTidsplan} da="Ifølge tidsplan" en="Ontrack" />
                        <FRPLegendLine color={FRPConst.processStatusColors.bagTidsplan} da="Bag tidsplan" en="Behind schedule" />
                        <FRPLegendLine color={FRPConst.processStatusColors.manglerTidsplan} da="Mangler tidsplan" en="Missing schedule" />
                        <FRPLegendLine color={FRPConst.processStatusColors.ingenStatus} da="Ingen status" en="No status" />
                     </div>
                  );
               case "lastChanged":
                  return (
                     <div>
                        <FRPLegendLine color={FRPConst.lastChangedColors.newest} da="Nyeste ændring" en="Latest change" />
                        <FRPLegendLine color={FRPConst.lastChangedColors.oldest} da="Ældste ændring" en="Oldest change" />
                     </div>
                  );
               case "tagsV2":
                  return (
                     <div>
                        {FRPState.tagOptions.map((element) => (
                           <FRPLegendLine key={element.id} color={element.color} da={element.val} en={element.val} />
                        ))}
                        <FRPLegendLine color={FRPConst.defaultColor} da="Ingen tags" en="No tags" />
                     </div>
                  )
               default:
                  return <span>Legend not defined for {props.visualization}</span>
            }
         })()}
      </>
   )
}